import React, { useState } from "react";
import Papa from "papaparse";
import apiInstance from "../../../utility/apiInstance";
import { useSelector } from "react-redux";

interface BulkUploadStudentsPopupProps {
  show: boolean;
  onClose: () => void;
  onBulkUploadComplete: (students: any[]) => void;
}

const BulkUploadStudentsPopup: React.FC<BulkUploadStudentsPopupProps> = ({
  show,
  onClose,
  onBulkUploadComplete,
}) => {
  const user = useSelector((state: any) => state.auth.user);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [completeData, setCompleteData] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const [msg, setMsg] = useState({
    error: false,
    loading: false,
    text: "",
  });

  const generateRandomPassword = (length = 12) => {
    return "Student@Instaadmissions";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      setMsg({
        error: true,
        loading: false,
        text: "Please upload a CSV file",
      });
      return;
    }

    setFile(selectedFile);
    setMsg({ error: false, loading: false, text: "" });

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (results) => {
        const cleanedData = results.data.map((row: any) => {
          const cleanRow: any = {};
          Object.keys(row).forEach((key) => {
            cleanRow[key.trim()] = row[key];
          });
          return cleanRow;
        });
        setPreview(cleanedData.slice(0, 5));
        setCompleteData(cleanedData);
      },
      error: (error) => {
        setMsg({
          error: true,
          loading: false,
          text: `CSV parsing error: ${error.message}`,
        });
      },
    });
  };

  const validateStudent = (student: any): string => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{7,15}$/;

    if (!student.firstName?.trim()) return "First name is required";
    if (!student.lastName?.trim()) return "Last name is required";
    if (!student.email?.trim() || !emailRegex.test(student.email))
      return "Valid email is required";

    // if (!student.phone?.trim() || !phoneRegex.test(student.phone))
    //   return "Valid phone number is required";
    if (!student.gender?.trim()) return "Gender is required";
    if (!student.dob || isNaN(new Date(student.dob).getTime()))
      return "Valid date of birth is required";
    if (!student.firstLanguage?.trim()) return "First language is required";
    if (!student.countryOfCitizenship?.trim())
      return "Country of citizenship is required";
    if (!student.passport?.trim()) return "Passport number is required";
    if (
      !student.passportExpiry?.trim() ||
      isNaN(new Date(student.passportExpiry).getTime())
    )
      return "Valid passport expiry date is required";

    return "";
  };

  const handleUpload = async () => {
    if (!file) {
      setMsg({
        error: true,
        loading: false,
        text: "Please select a file first",
      });
      return;
    }

    if (!consentConfirmed) {
      setMsg({
        error: true,
        loading: false,
        text: "Please confirm student consent",
      });
      return;
    }

    setUploading(true);
    setMsg({
      error: false,
      loading: true,
      text: "Processing CSV...",
    });

    try {
      const students = completeData.map((row: any) => {
        const cleanRow: any = {};
        Object.keys(row).forEach((key) => {
          cleanRow[key.trim()] = row[key];
        });
        return cleanRow;
      });

      setMsg({
        error: false,
        loading: true,
        text: `Uploading ${students.length} students...`,
      });

      const uploadedStudents = [];
      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      for (let i = 0; i < students.length; i++) {
        const student = students[i];

        try {
          // Map CSV columns to API fields
          const studentData = {
            firstName: student.firstName || student["First Name"],
            lastName: student.lastName || student["Last Name"],
            dob: student.dob || student["Date of Birth"] || student.DOB,
            gender: student.gender || student.Gender,
            countryOfCitizenship:
              student.countryOfCitizenship ||
              student["Country of Citizenship"] ||
              student.country,
            firstLanguage:
              student.firstLanguage || student["First Language"] || student.language,
            passport: student.passport || student["Passport Number"] || student.passportNumber,
            passportExpiry:
              student.passportExpiry || student["Passport Expiry"] || student.passportExpiryDate,
            email: student.email || student.Email,
            phone: student.phone || student.Phone || student["Phone Number"],
            role: "student",
            agent: user.id,
            password: generateRandomPassword(),
          };

          // Validate student data
          const validationError = validateStudent(studentData);
          if (validationError) {
            errors.push(
              `Row ${i + 1} (${studentData.email || "unknown"}): ${validationError}`
            );
            failCount++;
            continue;
          }

          const response = await apiInstance.post("/api/users", studentData);

          if (response) {
            const data = await response.data;
            uploadedStudents.push(data.doc);
            successCount++;
          } else {
            failCount++;
            errors.push(`Row ${i + 1}: Failed to create student`);
          }

          setMsg({
            error: false,
            loading: true,
            text: `Uploaded ${i + 1}/${students.length} students...`,
          });
        } catch (error: any) {
          console.error(`Failed to upload student: ${student.email}`, error);
          failCount++;
          errors.push(
            `Row ${i + 1} (${student.email || "unknown"}): ${error.response?.data?.message || error.message}`
          );
        }
      }

      let resultText = `Upload complete! Success: ${successCount}, Failed: ${failCount}`;
      if (errors.length > 0 && errors.length <= 5) {
        resultText += "\n\nErrors:\n" + errors.join("\n");
      } else if (errors.length > 5) {
        resultText += `\n\nShowing first 5 errors:\n${errors.slice(0, 5).join("\n")}\n...and ${errors.length - 5} more`;
      }

      setMsg({
        error: failCount > 0,
        loading: false,
        text: resultText,
      });

      if (uploadedStudents.length > 0) {
        onBulkUploadComplete(uploadedStudents);
      }

      setTimeout(() => {
        if (failCount === 0) {
          onClose();
        }
      }, 3000);
    } catch (error: any) {
      setMsg({
        error: true,
        loading: false,
        text: `Upload failed: ${error.message}`,
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `firstName,lastName,dob,gender,countryOfCitizenship,firstLanguage,passport,passportExpiry,email,phone
John,Doe,2000-05-15,male,NZ,English,AB1234567,2030-12-31,john.doe@example.com,1234567890
Jane,Smith,1998-08-22,female,CN,English,CD7891234,2029-06-30,jane.smith@example.com,9876543210`;

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student_upload_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title text-secondary">Bulk Upload Students</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={uploading}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="card mb-4 shadow-sm border-0 rounded-3">
              <div className="card-body">
                {/* Instructions */}
                <div className="alert alert-info mb-4">
                  <h6 className="fw-bold mb-2">Instructions:</h6>
                  <ol className="mb-0 ps-3">
                    <li>Download the CSV template below</li>
                    <li>Fill in your student data following the template format</li>
                    <li>Upload the completed CSV file</li>
                    <li>Review the preview and confirm consent</li>
                    <li>Click "Upload Students"</li>
                  </ol>
                  <button
                    className="btn btn-sm btn-outline-primary mt-3"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-download me-2"></i>
                    Download CSV Template
                  </button>
                </div>

                {/* File Upload */}
                <div className="mb-4">
                  <label className="form-label fw-bold">
                    Select CSV File <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    className="form-control"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                  {file && (
                    <small className="text-muted d-block mt-2">
                      Selected: {file.name}
                    </small>
                  )}
                </div>

                {/* Preview Table */}
                {preview.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Preview (First 5 rows):</h6>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      <table className="table table-bordered table-sm">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Country</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.map((row, index) => (
                            <tr key={index}>
                              <td>{row.firstName || row["First Name"] || "-"}</td>
                              <td>{row.lastName || row["Last Name"] || "-"}</td>
                              <td>{row.email || row.Email || "-"}</td>
                              <td>{row.phone || row.Phone || "-"}</td>
                              <td>
                                {row.countryOfCitizenship ||
                                  row["Country of Citizenship"] ||
                                  row.country ||
                                  "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <small className="text-muted">
                      Total students in file: {completeData.length}
                    </small>
                  </div>
                )}

                {/* Consent Checkbox */}
                {preview.length > 0 && (
                  <div className="mb-4">
                    <div className="d-flex align-items-start gap-3">
                      <input
                        className="form-check-input mt-1"
                        type="checkbox"
                        id="bulkConsentCheck"
                        checked={consentConfirmed}
                        onChange={(e) => setConsentConfirmed(e.target.checked)}
                        disabled={uploading}
                      />
                      <label
                        className="form-check-label fs-14"
                        htmlFor="bulkConsentCheck"
                      >
                        <strong>I confirm that I have received express written consent</strong> from
                        all {completeData.length} students listed in this CSV file and I can
                        provide proof of their consent upon request.
                      </label>
                    </div>
                  </div>
                )}

                {/* CSV Format Requirements */}
                <div className="alert alert-warning">
                  <h6 className="fw-bold mb-2">Required CSV Columns:</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <ul className="mb-0 ps-3 small">
                        <li>firstName (required)</li>
                        <li>lastName (required)</li>
                        <li>email (required, valid format)</li>
                        <li>phone (required, 7-15 digits)</li>
                        <li>gender (required)</li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <ul className="mb-0 ps-3 small">
                        <li>dob (required, YYYY-MM-DD)</li>
                        <li>countryOfCitizenship (required)</li>
                        <li>firstLanguage (required)</li>
                        <li>passport (required)</li>
                        <li>passportExpiry (required, YYYY-MM-DD)</li>
                      </ul>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">
                    Note: Passwords will be auto-generated for all students
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <div className="w-100">
              {msg.text && (
                <div
                  className={`alert ${
                    msg.error ? "alert-danger" : "alert-success"
                  } mb-3`}
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    whiteSpace: "pre-line",
                  }}
                >
                  {msg.text}
                </div>
              )}
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={!file || !consentConfirmed || uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload me-2"></i>
                      Upload Students
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadStudentsPopup;